import { connectivityProximityPresentation, resolveNrcProximity } from '../../../lib/reportSourceEvidence';
import { formatProtectedAreaEvidence } from '../../../lib/padusEvidence';
import { NwiAcreageConflictPanel } from '../NwiAcreageConflictPanel';
import { researchConsultationLabel } from '../../../services/dataProviders/siteResearch';
import { qualifiedLocalSentimentEntries } from '../../../lib/sentiment/qualifiedLocalSentiment';
import { psrReportView } from '../../../lib/sentiment/psrReportView';
import { SentimentResearchBlocks } from '../sections/PublicSentiment/viz';
import React from 'react';
import type { SiteRagReport } from '../../../types/siteRag';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import { formatElectricityPricingTitle, formatElectricityPriceReference, formatWetlandsDisplay } from '../../../lib/reportFormatters';
import { ReportSectionProps } from './types';
import { DataCell } from './DataCell';
import { EditableHeading } from './EditableHeading';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { formatDoctrineDisplay } from '../../../lib/formatDoctrine';
import { resolveSiteElevation } from '../../../lib/resolveSiteElevation';
import { ElevationDiagnostic } from './ElevationDiagnostic';
import {
  describeElevationEmpty,
  type ElevationEmptyDiagnosis,
} from '../../../lib/classifyElevationEmpty';
import { EditorialSectionShell } from './EditorialSectionShell';
import { GENERIC_SECTION_INSUFFICIENT_MESSAGE } from '../../../lib/sectionFallbacks/derivedFallbacks';
import type { ReportTheme } from '../../../types/reportTheme';
import { roleColor, roleColorAlpha, roleBorder, type ReportColorRole } from '../../../lib/reportRoleColors';
import { editorialFlat, reportHeadingInk } from '../../../lib/reportSectionHeading';
import { reportSectionGapClass } from '../../../lib/reportSpacingTokens';
import { formatNumber, type ThemeLocale } from '../../../lib/themeFormatters';
import { EditorialDatasheet } from './editorial';
import type { DatasheetGroup, DatasheetRow } from './editorial';
// COMBINES (fold #5) — the appendix-subsection head reuses ReferencesSection's
// small-caps `appendixHeadStyle` idiom so the folded "Extended Site Data"
// sub-head matches its appendix siblings ("Data Sources & References",
// "Data quality notes", "Documents") exactly.
import { appendixHeadStyle } from './ReferencesSection';
import { layoutGroupProps } from '../../../lib/reportRendering/layoutGroups';
import { neutralizeUnverifiedWaterRegulatoryThresholdText } from '../../../lib/audit/waterUseScreeningGuard';

const MONO = 'var(--report-body-font, inherit)';

/**
 * COMBINES (fold #5) — shared content predicate for the appendix fold. Mirrors
 * the component's own `hasExtendedData` gate EXACTLY (the main non-PSR render
 * branch) so the three render decisions stay in lockstep: the fold's
 * suppress-when-empty guard here, the caller's slot construction
 * (SiteRagReportView passes `extendedDataSlot` only when this is true, so
 * ReferencesSection's bare-heading guard can count the slot as a renderable
 * child), and the DOCX/PPTX appendix folds. Same pattern as
 * `capacityCreditDeepDiveHasContent` for the gridStudy ELCC fold (fold #3):
 * no content ⇒ no sub-head, no empty box in the appendix.
 */
export function extendedDataHasContent(
  report: Pick<SiteRagReport, 'datasets'>,
): boolean {
  const ds = (report.datasets ?? {}) as NonNullable<SiteRagReport['datasets']>;
  return !!(
    ds.seismic || ds.solar || ds.wind || ds.electricity || ds.airQuality
    || ds.connectivity || ds.parcel || ds.environmental || ds.nrcReactors
    || ds.siteResearch
  );
}

/** Hairline-bordered data box on the cream page. FLATTEN-TO-CREAM: the grey
 *  `subtleBg` wash is removed (transparent), so a boxed group (Site Boundary,
 *  sentiment scan, tribal/permitting/precedents) reads as ink-on-cream with a
 *  thin hairline rule — matching the clean Electricity-Pricing / Renewable
 *  datasheet rows that render without any box fill. Theme-aware.
 *
 *  N1.5C-flat (EDITORIAL-MODE ONLY) — when `editorialActive` is ON the box loses
 *  its all-sides 1px border + `borderRadius:6` and routes through the ONE
 *  `editorialFlat()` open-block seam: a `borderTop:1px solid rule` hairline +
 *  `paddingTop` (no enclosing box). Flag OFF keeps the bordered hairline box so
 *  the flag-OFF surface is byte-identical. */
function dataBoxStyle(theme: ReportTheme, editorialActive = false): React.CSSProperties {
  if (editorialActive) {
    return {
      background: 'transparent',
      ...editorialFlat(theme.density === 'compact', {
        rule: roleColor(theme, 'rule'),
        accent: roleColor(theme, 'accent'),
        muted: roleColor(theme, 'muted'),
      }).topRule,
    };
  }
  return {
    background: 'transparent',
    border: `1px solid ${roleColor(theme, 'hair')}`,
    borderRadius: 6,
  };
}

/** Small-caps mono micro-label (replaces text-[9px/10px] font-bold
 *  uppercase tracking-widest text-text-muted/secondary). */
function microLabelStyle(theme: ReportTheme, role: ReportColorRole = 'muted'): React.CSSProperties {
  return {
    fontFamily: MONO,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: roleColor(theme, role),
  };
}

/** Restrained small-caps status tag — status ink + low-alpha border,
 *  replacing the filled green/warning/danger pills. */
function statusTagStyle(theme: ReportTheme, role: ReportColorRole): React.CSSProperties {
  return {
    fontFamily: MONO,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: roleColor(theme, role),
    border: `1px solid ${roleBorder(theme, role)}`,
  };
}

// TS3 (headingScale) + TS8 (themeTypographyScale) — derived INLINE
// (no cross-cluster helper import). `headingScale` picks a base h2 rem;
// `themeTypographyScale` (0.875|1|1.125|1.25) multiplies it. Legacy
// themes missing either field fall through to the editorial baseline so
// existing reports render unchanged. Applied as inline `fontSize` on
// the section <h2> (which keeps its `text-xl` class) so the size now
// tracks the active theme instead of being pinned to text-xl.
const EXTENDED_HEADING_H2_REM: Record<
  'editorial' | 'technical' | 'compact',
  number
> = {
  editorial: 1.5,
  technical: 1.25,
  compact: 1.05,
};

function extendedHeadingFontSize(
  theme: { headingScale?: 'editorial' | 'technical' | 'compact'; themeTypographyScale?: number },
): string {
  const scale =
    theme.headingScale === 'technical' || theme.headingScale === 'compact'
      ? theme.headingScale
      : 'editorial';
  const base = EXTENDED_HEADING_H2_REM[scale];
  const mult =
    typeof theme.themeTypographyScale === 'number' &&
    Number.isFinite(theme.themeTypographyScale) &&
    theme.themeTypographyScale > 0
      ? theme.themeTypographyScale
      : 1;
  return `${Math.round(base * mult * 1000) / 1000}rem`;
}

function formatSentimentScore(score: number): string {
  if (!Number.isFinite(score)) return '–';
  const pct = Math.round((score + 1) * 50);
  return `${score >= 0 ? '+' : ''}${score.toFixed(2)} (${pct}/100)`;
}

const PSR_TECH_LABELS: Record<string, string> = {
  'nuclear-smr': 'Nuclear / SMR',
  'natural-gas': 'Natural gas',
  'solar-bess': 'Solar + BESS',
  'wind-bess': 'Wind + BESS',
  'general-datacenter': 'General data center',
};

function psrTechLabel(tech: string): string {
  return PSR_TECH_LABELS[tech] ?? tech;
}

function fmtFixed(value: unknown, digits: number, fallback = '–'): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(digits) : fallback;
}

type ExtendedDataSectionProps = ReportSectionProps & {
  /**
   * ELV-1 — live site evidence document. The Site Boundary
   * accordion's elevation rows fall back to
   * `sed.siteProfile.data.elevationAvg` when the report's persisted
   * `siteElevationRange` is the legacy `{0,0,0}` fingerprint (Redhead
   * + every pre-EL2 site). Optional — callers without a resolved SED
   * pass undefined and the accordion shows "Data unavailable" +
   * Refresh button (or hides cleanly).
   */
  sed?: SiteEvidenceDocument | null;
  /**
   * ELV-2 — handler for the "Refresh elevation" CTA. When wired,
   * surfaces a button next to the elevation rows; clicking it
   * triggers a re-fetch via `refreshSiteElevation` (Google polygon /
   * centroid → persist via `updateSite`). Optional — legacy
   * surfaces without a live SiteEntity to write to (free-standing
   * report viewers) leave it undefined and the button hides.
   */
  onRefreshElevation?: () => void | Promise<void>;
  /** Set true while the refresh is in-flight; disables the button + shows a spinner. */
  isRefreshingElevation?: boolean;
  /**
   * ELV-4 — epoch-ms timestamp of the most recent successful
   * elevation refresh on the LIVE SiteEntity (i.e.
   * `liveSite.siteElevationRange.refreshedAt`). Surfaces below the
   * elevation cells as "last refreshed N ago" in the diagnostic.
   * Optional — undefined ⇒ chip omits the timestamp (legacy sites
   * that never refreshed since ELV-4 shipped).
   */
  siteElevationRefreshedAt?: number;
  /**
   * ELV-4 — diagnostic classification when the most recent elevation
   * fetch returned no usable samples (e.g. Davenport, IA). When passed
   * AND the elevation cells are rendering the empty-state path, a
   * `data-testid="elevation-empty-reason"` chip appears beneath the
   * Refresh CTA with a short root-cause description + optional hint
   * + "Last attempted {ago}" suffix.
   *
   * Pass `null`/`undefined` to suppress the chip (legacy report
   * viewers don't know what was last attempted).
   */
  elevationEmptyDiagnosis?: ElevationEmptyDiagnosis | null;
  /**
   * ELV-4 — wall-clock timestamp of the most recent elevation fetch
   * attempt (ISO string or epoch ms). Formatted as a relative
   * "Last attempted {ago}" suffix on the diagnostic chip. Optional —
   * surfaces the classification with no timestamp suffix when absent.
   */
  elevationLastAttemptedAt?: string | number | null;
  sectionNumberProps?: { 'data-section-number'?: string };
  /** N1.5 — VITE_EDITORIAL_LAYOUTS on: render the editorial hero-numeral opener. */
  editorialActive?: boolean;
  /** N1.5 — 1-based TOC display number for the hero numeral (NaN ⇒ none). */
  editorialSectionNumber?: number;
  /**
   * COMBINES (fold #5) — render as a SUBSECTION of the Appendix & References
   * section instead of a standalone numbered section. When true:
   *   (a) the EditorialSectionShell + section number / hero numeral are
   *       DROPPED (the appendix shell owns the section chrome; visibility +
   *       order are parent-owned, so the `isVisible`/`sectionStyle` gates are
   *       skipped — the caller gates slot construction on
   *       `extendedDataHasContent` + the archived `sectionVisibility` hide);
   *   (b) a small-caps sub-head in ReferencesSection's `appendixHeadStyle`
   *       idiom renders instead ("Extended Site Data", heading-override aware
   *       via the same EditableHeading key);
   *   (c) the wrapper KEEPS `data-section="extendedData"` — a NESTED
   *       data-section inside the appendix is the established reconcile-safe
   *       shape (same as `dataSources`, and the gridStudy ELCC fold #3): the
   *       export DOM reorder + pagination snapshot logic only walk TOP-LEVEL
   *       section nodes, so the nested box travels with the appendix.
   * Content is empty-suppressed (no sub-head over an empty datasheet) —
   * mirroring the ELCC fold's EMPTY-SECTION SUPPRESSION. Public-sentiment
   * NEVER sets this: its `sentimentResearchDetail` variant stays a standalone
   * section. Default false ⇒ the legacy standalone render, byte-identical.
   */
  renderAsAppendixSubsection?: boolean;
  /**
   * RC5 — resolved locale/units from the presentation contract
   * (`presentation.locale`). Routes the on-screen number formatting (e.g. the
   * parcel assessed value) through `themeFormatters` so it is locale-faithful.
   * Absent ⇒ the formatter defaults (US-EN / 1,234.56), which match the prior
   * hardcoded `toLocaleString('en-US')` grouping for integer values
   * (byte-stable for the common case).
   */
  locale?: ThemeLocale;
};

/** Short relative-time formatter — pure, no I/O. Returns e.g. "2 minutes ago"
 *  or "just now". Used by the ELV-4 diagnostic chip. */
function formatAgo(at: string | number | null | undefined): string | null {
  if (at == null) return null;
  const ts = typeof at === 'number' ? at : Date.parse(at);
  if (!Number.isFinite(ts)) return null;
  const deltaSec = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (deltaSec < 5) return 'just now';
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const m = Math.round(deltaSec / 60);
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

/** Collapsible subsection — open by default so readers see every
 *  subsection's content on first render; click the header to collapse
 *  an individual section. Inherits font from parent.
 *
 *  KMU — optional `rightSlot` slots interactive content at the right edge
 *  of the header without toggling collapse. Clicks inside the slot stop
 *  propagation so the user can interact without folding the section. */
function AccordionTab({
  title,
  defaultOpen = true,
  rightSlot,
  children,
  theme,
  editorialActive = false,
  collapsible = true,
  pagedStaticGroupId,
}: {
  title: string;
  defaultOpen?: boolean;
  rightSlot?: React.ReactNode;
  children: React.ReactNode;
  theme: ReportTheme;
  editorialActive?: boolean;
  /** BUG-9A — when false, the tab is permanently expanded and non-interactive:
   *  no disclosure chevron and no click-to-toggle. The content always renders.
   *  Used for report-surface blocks (e.g. "Site-Specific Research") where a
   *  collapsible chevron must not leak into the static/PDF read-only view.
   *  Defaults true so every other accordion keeps its existing behaviour. */
  collapsible?: boolean;
  /**
   * Stable identity used to verify an immutable reading group's complete text
   * across every Paged.js fragment. Required by every non-collapsible report
   * group so recovery never confuses two similarly titled or split groups.
   */
  pagedStaticGroupId?: string;
}) {
  if (!collapsible && !pagedStaticGroupId) {
    throw new Error('Non-collapsible report groups require a stable pagedStaticGroupId.');
  }
  // When non-collapsible the tab is always open; `defaultOpen` is ignored.
  const [open, setOpen] = React.useState(defaultOpen);
  const expanded = collapsible ? open : true;
  // N1.5C-flat (EDITORIAL-MODE ONLY) — drop the `rounded-lg`/`overflow-hidden`
  // Aurora card edge + the 1px box border. Collapsible dashboard accordions keep
  // the `data-flow-card` atomicity hook; permanently expanded report datasheets
  // deliberately do not (they must fragment between rows). Groups are separated
  // by a `borderTop:1px solid rule` open rule above the micro-label header instead
  // of being enclosed in a box. Flag OFF keeps the bordered card treatment.
  return (
    // data-flow-card: under the two-column journal engine, each COLLAPSIBLE
    // accordion is a discrete unit and must NOT split across the column break
    // (the report column engine applies break-inside:avoid to [data-flow-card]).
    // Permanently expanded report datasheets are intentionally excluded: they can
    // be taller than one page and must fragment between their row atoms.
    //
    // P-GROUP Phase A (`row-group`) — a NON-COLLAPSIBLE report-surface tab
    // (e.g. "Site-Specific Research") is a TALL datasheet container: the
    // [data-flow-card] break-inside:avoid made it one unfragmentable box, so a
    // tab taller than a page CLIPPED mid-row in the paged deck with the tail
    // LOST (the live SITE-SPECIFIC RESEARCH failure). Stamping `row-group`
    // makes the deck treat the container as fragmentable; omitting the mutually
    // contradictory flow-card marker removes the atomic rule at its source, and
    // the EditorialDatasheet rows inside carry their own `row` atoms. Interactive
    // (collapsible) accordions keep the flow-card contract untouched.
    <div
      data-flow-card={collapsible ? true : undefined}
      data-layout-group={collapsible ? undefined : 'row-group'}
      {...(collapsible ? {} : { 'data-paged-static-reading-group': 'true' })}
      {...(collapsible || !pagedStaticGroupId
        ? {}
        : { 'data-paged-static-reading-group-id': pagedStaticGroupId })}
      // P-GROUP overflow guard — `overflow:hidden` makes a box MONOLITHIC (CSS
      // fragmentation), which pagedjs cannot split REGARDLESS of the
      // `break-inside:auto !important` the `row-group` deck rule sets: the
      // Site-Specific Research datasheet then CLIPPED mid-row at the page edge
      // with its tail LOST (the live failure). The `row-group` marker alone did
      // not cure it because `overflow:hidden` co-lived on the SAME box. So the
      // non-collapsible REPORT surface drops `overflow-hidden` (keeps
      // `rounded-lg` for the border-radius) → the container stays fragmentable
      // and the tall datasheet flows across pages. The collapsible (dashboard)
      // case keeps the Aurora rounded-corner clip byte-identical.
      className={editorialActive ? '' : (collapsible ? 'rounded-lg overflow-hidden' : 'rounded-lg')}
      style={
        editorialActive
          ? { borderTop: `1px solid ${roleColor(theme, 'rule')}` }
          : { border: `1px solid ${roleColor(theme, 'hair')}` }
      }
    >
      <div
        className="w-full flex items-center justify-between transition-colors"
        style={{ fontFamily: 'inherit' }}
        // BUG-9A — only show the hover affordance when the tab is actually
        // interactive; a non-collapsible header must not imply it can be clicked.
        onMouseEnter={collapsible ? (e) => { e.currentTarget.style.background = roleColor(theme, 'subtleBg'); } : undefined}
        onMouseLeave={collapsible ? (e) => { e.currentTarget.style.background = 'transparent'; } : undefined}
      >
        {collapsible ? (
          <button
            type="button"
            className="flex-1 flex items-center justify-between text-left bg-transparent px-3 py-2 text-sm font-bold uppercase tracking-widest"
            style={{ fontFamily: MONO, letterSpacing: '0.14em', color: roleColor(theme, 'ink') }}
            onClick={() => setOpen(!open)}
          >
            {title}
            {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        ) : (
          // BUG-9A — non-collapsible: a plain header (no <button>, no chevron,
          // no toggle) so the disclosure control never leaks into the report.
          <div
            className="flex-1 text-left px-3 py-2 text-sm font-bold uppercase tracking-widest"
            style={{ fontFamily: MONO, letterSpacing: '0.14em', color: roleColor(theme, 'ink') }}
            data-paged-static-reading-group-heading="true"
          >
            {title}
          </div>
        )}
        {rightSlot && (
          <div
            className="pr-3 flex items-center shrink-0"
            // Stop propagation so the menu's chevron / popover clicks
            // don't bubble up and toggle the accordion.
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {rightSlot}
          </div>
        )}
      </div>
      {expanded && (
        <div
          className="px-3 pb-3 pt-1"
          style={{ fontFamily: 'inherit' }}
          {...(collapsible || !pagedStaticGroupId
            ? {}
            : { 'data-paged-static-reading-group-body': 'true' })}
        >
          {children}
        </div>
      )}
    </div>
  );
}

const ExtendedDataSection = React.memo<ExtendedDataSectionProps>(({
  report, theme, isEditing, editedReport, setEditedReport,
  headingOverrides, setHeading, isVisible, sectionStyle,
  omitSectionSidebar = false,
  shellPadding = true,
  sectionPageColumnCount,
  sectionNumberProps,
  editorialActive = false,
  editorialSectionNumber,
  // BUG-10 — `onRefreshElevation` is no longer destructured/rendered (the
  // Refresh CTA was removed from the report surface); it stays on the prop
  // type for caller-contract compatibility. `isRefreshingElevation` is still
  // consumed by the diagnostic-chip suppression below.
  sed, isRefreshingElevation,
  siteElevationRefreshedAt,
  elevationEmptyDiagnosis, elevationLastAttemptedAt,
  locale,
  renderAsAppendixSubsection = false,
}) => {
  // PH-A2 #8: `datasets` is optional on SiteRagReport. The editor
  // section bails out entirely when no datasets exist (metadata-only
  // fallback path). The explicit cast preserves the legacy read-side
  // ergonomics — every inner access below was already gated on
  // `ds.<key> &&` and tolerated undefined inner fields.
  const ds = (report.datasets ?? {}) as NonNullable<SiteRagReport['datasets']>;
  // Same cast for the edited shape — the editor only opens against a
  // full report (the metadata-only path never reaches the editor UI),
  // but the type contract now requires us to either guard or assert.
  const editedDatasets = (editedReport.datasets ?? {}) as NonNullable<SiteRagReport['datasets']>;
  const isPsr = report.reportType === 'public-sentiment';
  const sectionId = isPsr ? 'sentimentResearchDetail' : 'extendedData';
  const sectionLabel = isPsr ? 'Sentiment Research Detail' : 'Extended Site Data';
  // COMBINES (fold #5) — appendix-subsection mode is a NON-PSR contract
  // (public-sentiment keeps its standalone `sentimentResearchDetail` section);
  // the `!isPsr` guard is defensive against a mis-wired caller.
  const appendixFold = renderAsAppendixSubsection && !isPsr;
  // In fold mode visibility + order are PARENT-OWNED: `extendedData` is no
  // longer a registry section for this type, so `isVisible('extendedData')`
  // would be unconditionally false (type-applicability gate). The caller
  // (SiteRagReportView) gates slot construction on the archived
  // `sectionVisibility` hide + `extendedDataHasContent` instead.
  if (!appendixFold && !isVisible(sectionId)) return null;

  const ps = sed?.publicSentiment;
  const meta = report.publicSentimentMeta;
  const psrView = isPsr ? psrReportView(report) : undefined;
  const localStances = sed?.id === report.sedId
    && ps?.queryContext?.fingerprint === meta?.queryContext?.fingerprint
    ? qualifiedLocalSentimentEntries(ps) : [];
  const techKeys = new Set([...(ps?.perTech ?? []).map(bucket => bucket.btmTech), ...localStances.map(row => row.btmTech)]);
  const techStances = [...techKeys].map(btmTech => {
    const rows = localStances.filter(row => row.btmTech === btmTech);
    const weight = rows.reduce((sum, row) => sum + row.confidence, 0);
    return { btmTech, count: ps?.perTech?.find(bucket => bucket.btmTech === btmTech)?.count
      ?? (ps?.entries ?? []).filter(row => row.btmTech === btmTech).length, qualifiedCount: rows.length,
      localMean: weight ? rows.reduce((sum, row) => sum + row.sentimentScore * row.confidence, 0) / weight : undefined };
  });
  const hasSentimentResearch = !!(
    ps
    || meta
    || ds.siteResearch?.precedents?.length
    || ds.siteResearch?.statePermitting
    || ds.siteResearch?.tribalLands?.consultationRequired
    || (ds.siteResearch?.tribalLands?.nearbyTribes?.length ?? 0) > 0
  );

  // TSP-1 — fold sectionStyle into the inline style bag so the Studio's
  // drag-reorder + eye-toggle propagate to this extracted section.
  const studioStyle: React.CSSProperties = sectionStyle(sectionId);

  // TS3 + TS8 — theme-driven h2 size for this section's heading. The
  // <h2> keeps `text-xl`; the inline fontSize overrides it so the size
  // tracks `headingScale` × `themeTypographyScale`.
  const headingFontSize = extendedHeadingFontSize(theme);

  // N1.5C — the heading node is SHARED between the two shell modes. In editorial
  // mode the shell owns the opener (hero numeral + numbered head); in classic
  // mode the caller renders `classicHead` below (byte-identical to the retired
  // ClassicSectionShell path).
  const headingNode = (
    <EditableHeading
      headingKey={sectionId}
      defaultText={sectionLabel}
      className="text-xl font-bold"
      isEditing={isEditing}
      overrides={headingOverrides}
      onChange={setHeading}
    />
  );
  const classicHead = !editorialActive ? (
    <h2
      className="text-xl font-bold border-b-2 pb-2"
      style={{ borderColor: reportHeadingInk(theme), color: reportHeadingInk(theme), fontFamily: theme.headingFont, fontSize: headingFontSize }}
      data-heading="true"
    >
      {headingNode}
    </h2>
  ) : null;
  // The N1.5C two-mode shell props common to every return branch below.
  const shellModeProps = {
    editorialActive,
    sectionNumber: editorialSectionNumber,
    heading: headingNode,
  } as const;

  if (isPsr) {
    if (!hasSentimentResearch) {
      return (
        <EditorialSectionShell
          id={sectionId}
          data-page="extended"
          data-section={sectionId}
          data-section-id={sectionId}
          data-section-columns={sectionPageColumnCount ? String(sectionPageColumnCount(sectionId)) : undefined}
          extraClassName={reportSectionGapClass(theme.density)}
          style={{ ...studioStyle, fontFamily: theme.bodyFont, color: roleColor(theme, 'body'), scrollMarginTop: '80px', borderBottom: `1px solid ${roleColor(theme, 'hair')}` }}
          omitSectionSidebar={omitSectionSidebar}
          shellPadding={shellPadding}
          theme={theme}
          {...sectionNumberProps}
          {...shellModeProps}
        >
          <div className="space-y-3">
            {classicHead}
            <div className="text-xs italic px-3 py-2" style={{ ...dataBoxStyle(theme, editorialActive), color: roleColor(theme, 'muted') }} data-testid="extended-data-empty">
              No public-sentiment observations yet – run PSE prefetch on Site Evidence, then regenerate.
            </div>
          </div>
        </EditorialSectionShell>
      );
    }

    return (
      <EditorialSectionShell
        id={sectionId}
        data-page="extended"
        data-section={sectionId}
        data-section-id={sectionId}
        data-section-columns={sectionPageColumnCount ? String(sectionPageColumnCount(sectionId)) : undefined}
        extraClassName={reportSectionGapClass(theme.density)}
        style={{ ...studioStyle, fontFamily: theme.bodyFont, color: roleColor(theme, 'body'), scrollMarginTop: '80px', borderBottom: `1px solid ${roleColor(theme, 'hair')}` }}
        omitSectionSidebar={omitSectionSidebar}
        shellPadding={shellPadding}
        theme={theme}
        {...sectionNumberProps}
        {...shellModeProps}
      >
        <div className="space-y-3">
          {classicHead}
          <div className="space-y-2" data-testid="extended-data-sentiment">
            {/* PSR redesign — a report carrying the psr-meta-v2 projection shows
                 its frozen coverage matrix, archive read-through, reasons and
                 supporting tables in place of the live-SED scan summary. The
                 legacy accordion below renders only without the projection. */}
            {psrView && <SentimentResearchBlocks theme={theme} view={psrView} report={report} />}
            {!psrView && (ps || meta) && (
              <AccordionTab
                title={`Public Sentiment Scan – ${meta?.entryCount ?? ps?.entries?.length ?? 0} observations`}
                defaultOpen={true}
                theme={theme}
                editorialActive={editorialActive}
                // The static report/PDF is an immutable reading surface: an
                // interactive overflow-hidden disclosure is both misleading
                // and a fragmentation barrier (Paged.js can drop its expanded
                // body at a page seam). Keep edit mode interactive, but render
                // the complete scan as an always-open fragmentable group in
                // every canonical read/export surface.
                collapsible={isEditing}
                pagedStaticGroupId="public-sentiment-scan"
              >
                <div className="p-2.5 space-y-2.5" style={dataBoxStyle(theme, editorialActive)}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2">
                    <DataCell label="Scan status" value={ps?.status ?? meta?.status ?? '–'} />
                    <DataCell label="Sources attempted" value={String(meta?.sourcesAttempted ?? ps?.sourcesAttempted?.length ?? 0)} />
                    <DataCell label="Observations" value={String(meta?.entryCount ?? ps?.entries?.length ?? 0)} />
                    <DataCell
                      label="Opposed tech buckets"
                      value={localStances.length ? String(techStances.filter(t => t.localMean !== undefined && t.localMean <= -0.2).length) : 'Not established'}
                      highlight={techStances.some(t => t.localMean !== undefined && t.localMean <= -0.2)}
                    />
                  </div>
                  {techStances.length > 0 && (
                    <div className="pt-2 space-y-2" style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}` }}>
                      <div className="text-[10px] font-bold uppercase tracking-widest" style={microLabelStyle(theme)}>Per-technology sentiment</div>
                      <div className="grid grid-cols-1 gap-2">
                        {techStances.map((bucket) => (
                          <div key={bucket.btmTech} className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-text-secondary">
                            <span className="font-medium text-text-primary">{psrTechLabel(bucket.btmTech)}</span>
                            <span>{bucket.count} records · {bucket.qualifiedCount} local stances</span>
                            <span>{bucket.localMean !== undefined ? formatSentimentScore(bucket.localMean) : 'No local stance established'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {ps?.reason && (
                    <div className="text-xs italic pt-2" style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}`, color: roleColor(theme, 'muted') }}>{ps.reason}</div>
                  )}
                </div>
              </AccordionTab>
            )}

            {ds.siteResearch && (
              <AccordionTab
                title="Community & Permitting Research (AI-Sourced – Verify)"
                defaultOpen={true}
                theme={theme}
                editorialActive={editorialActive}
                collapsible={isEditing}
                pagedStaticGroupId="community-permitting-research"
              >
                <div className="space-y-3">
                  {(ds.siteResearch?.tribalLands?.consultationRequired || (ds.siteResearch?.tribalLands?.nearbyTribes?.length ?? 0) > 0) && (
                    <div className="space-y-2 p-2.5" style={dataBoxStyle(theme, editorialActive)}>
                      <div className="flex items-center justify-between pb-1" style={{ borderBottom: `1px solid ${roleColor(theme, 'hair')}` }}>
                        <div className="text-[10px] font-bold uppercase tracking-widest" style={microLabelStyle(theme)}>Tribal Consultation</div>
                        <span
                          className="px-2 py-0.5 text-[9px] font-bold uppercase"
                          style={statusTagStyle(theme, ds.siteResearch?.tribalLands?.consultationRequired === false ? 'positive' : 'warning')}
                        >
                          {researchConsultationLabel(ds.siteResearch?.tribalLands?.consultationRequired).toUpperCase()}
                        </span>
                      </div>
                      {ds.siteResearch?.tribalLands?.nearbyTribes?.length > 0 && (
                        <div className="text-xs text-text-secondary leading-relaxed">{ds.siteResearch.tribalLands.nearbyTribes.join(', ')}</div>
                      )}
                      {ds.siteResearch.tribalLands.details && (
                        <div className="text-xs text-text-muted leading-relaxed">{ds.siteResearch.tribalLands.details}</div>
                      )}
                    </div>
                  )}

                  {ds.siteResearch?.statePermitting && (
                    <div className="space-y-2 p-2.5" style={dataBoxStyle(theme, editorialActive)}>
                      <div className="text-[10px] font-bold uppercase tracking-widest pb-1" style={{ ...microLabelStyle(theme), borderBottom: `1px solid ${roleColor(theme, 'hair')}` }}>State Permitting Posture</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2">
                        <DataCell
                          label="Nuclear moratorium"
                          value={ds.siteResearch.statePermitting.nuclearMoratorium === true ? 'Yes' : ds.siteResearch.statePermitting.nuclearMoratorium === false ? 'No' : 'Unverified'}
                          highlight={ds.siteResearch.statePermitting.nuclearMoratorium === true}
                        />
                        <DataCell label="Estimated timeline" value={ds.siteResearch.statePermitting.estimatedTimeline} />
                      </div>
                      {ds.siteResearch?.statePermitting?.keyPermits?.length > 0 && (
                        <div className="text-xs text-text-secondary leading-relaxed">{ds.siteResearch.statePermitting.keyPermits.join(', ')}</div>
                      )}
                    </div>
                  )}

                  {ds.siteResearch?.precedents?.length > 0 && (
                    <div className="space-y-2 p-2.5" style={dataBoxStyle(theme, editorialActive)}>
                      <div className="text-[10px] font-bold uppercase tracking-widest pb-1" style={{ ...microLabelStyle(theme), borderBottom: `1px solid ${roleColor(theme, 'hair')}` }}>Siting Precedents</div>
                      <div className="space-y-3">
                        {ds.siteResearch.precedents.map((p, i) => (
                          <div key={i} className="space-y-0.5">
                            <div className="text-xs font-medium text-text-secondary leading-relaxed">{p.description}</div>
                            <div className="text-[10px] text-text-muted leading-relaxed italic">{p.relevance}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </AccordionTab>
            )}
          </div>
        </div>
      </EditorialSectionShell>
    );
  }

  // Single content gate shared with the fold callers (see extendedDataHasContent).
  const hasExtendedData = extendedDataHasContent(report);

  // COMBINES (fold #5) — EMPTY-SECTION SUPPRESSION in appendix mode: an empty
  // extended-data payload must not strand an "Extended Site Data" sub-head (or
  // an insufficient-data box) inside the appendix. Mirrors the gridStudy ELCC
  // fold's gate (no content ⇒ no heading). The standalone mode below keeps its
  // legacy empty-state box (it was a numbered TOC section that had to render).
  if (appendixFold && !hasExtendedData) return null;

  if (!hasExtendedData) {
    return (
      <EditorialSectionShell
        id={sectionId}
        data-page="extended"
        data-section={sectionId}
        data-section-id={sectionId}
        data-section-columns={sectionPageColumnCount ? String(sectionPageColumnCount(sectionId)) : undefined}
        extraClassName={reportSectionGapClass(theme.density)}
        style={{ ...studioStyle, fontFamily: theme.bodyFont, color: roleColor(theme, 'body'), scrollMarginTop: '80px', borderBottom: `1px solid ${roleColor(theme, 'hair')}` }}
        omitSectionSidebar={omitSectionSidebar}
        shellPadding={shellPadding}
        theme={theme}
        {...sectionNumberProps}
        {...shellModeProps}
      >
        <div className="space-y-3">
          {classicHead}
          <div className="text-xs italic px-3 py-2" style={{ ...dataBoxStyle(theme, editorialActive), color: roleColor(theme, 'muted') }} data-testid="extended-data-empty">
            {GENERIC_SECTION_INSUFFICIENT_MESSAGE}
          </div>
        </div>
      </EditorialSectionShell>
    );
  }

  // COMBINES (fold #5) — the datasheet/accordion body is IDENTICAL in both
  // render modes; only the chrome differs (standalone EditorialSectionShell +
  // numbered head vs. appendix sub-head). Built once so the fold can never
  // drift from the standalone content (SED elevation diagnostics, edit-mode
  // accordions, datasheets — all preserved wiring).
  const extendedBody = (
        <div className="space-y-2" {...layoutGroupProps('row-group')}>
          {/* 21A editorial datasheet — the read-only spec subsections
               (Seismic / Renewable / Pricing / Air Quality / Connectivity)
               are the "boxy collapsibles" William flagged. They are now
               composed as ONE EditorialDatasheet of grouped leader-dot rows
               (dense, scannable, print-clean, no cards) per the MAP 21A
               contract, replacing the per-section AccordionTab + dataBoxStyle
               cards. The datasheet primitive drops any row whose value is
               absent and omits an empty group, so a thin dataset renders
               fewer groups cleanly (no fabrication). The interactive /
               list-bearing subsections below (Environmental edit inputs,
               Parcel, NRC reactor list, Site Research, Site Boundary with the
               elevation diagnostics + Refresh CTA + edit inputs) KEEP their
               AccordionTab shells — the datasheet primitive cannot host edit
               fields, status logic, dot-lists, or the elevation diagnostic,
               so converting them would drop preserved wiring. */}
          {(() => {
            const groups: DatasheetGroup[] = [];

            if (ds.seismic) {
              const seismicRows: DatasheetRow[] = [
                { key: 'Design Category', value: ds.seismic.designCategory, tone: ds.seismic.designCategory >= 'C' ? 'warning' : 'default' },
                { key: 'PGA', value: `${fmtFixed(ds.seismic.pga, 3)} g` },
                { key: 'SDS', value: `${fmtFixed(ds.seismic.sds, 3)} g` },
                { key: 'SD1', value: `${fmtFixed(ds.seismic.sd1, 3)} g` },
              ];
              groups.push({
                heading: 'Seismic Design (ASCE 7-22)',
                meta: `SDC ${ds.seismic.designCategory} · Risk Cat ${ds.seismic.riskCategory}`,
                rows: seismicRows,
              });
            }

            if (ds.environmental && !isEditing) {
              const envRows: DatasheetRow[] = [
                { key: 'Critical Habitat', value: typeof ds.environmental.hasCriticalHabitat !== 'boolean' ? 'Unknown' : ds.environmental.hasCriticalHabitat ? 'Mapped final/proposed' : 'No mapped overlap', tone: ds.environmental.hasCriticalHabitat ? 'negative' : 'default' },
                { key: 'Wetlands', value: formatWetlandsDisplay(ds.environmental), tone: ds.environmental.hasWetlands ? 'warning' : 'default' },
              ];
              if (ds.environmental.protectedAreas) envRows.push({ key: 'PAD-US Inventory', value: formatProtectedAreaEvidence(ds.environmental.protectedAreas) });
              if (ds.environmental.species && ds.environmental.species.length > 0) {
                envRows.push({ key: 'Species', value: ds.environmental.species.join(', ') });
              }
              if (ds.environmental.wetlandTypes && ds.environmental.wetlandTypes.length > 0) {
                envRows.push({ key: 'Wetland Types', value: ds.environmental.wetlandTypes.join(', ') });
              }
              groups.push({
                heading: 'Environmental Constraints',
                meta: ds.environmental.hasCriticalHabitat ? 'CRITICAL HABITAT' : typeof ds.environmental.hasCriticalHabitat !== 'boolean' ? 'Habitat Unknown' : ds.environmental.hasWetlands === undefined ? 'Wetlands Unknown' : ds.environmental.hasWetlands ? 'Wetlands Present' : 'Environmental Screening',
                rows: envRows,
              });
            }

            if (ds.parcel && !isEditing) {
              const parcelRows: DatasheetRow[] = [
                { key: 'Zoning', value: ds.parcel.zoning },
                { key: 'Land Use', value: ds.parcel.landUse },
                { key: 'Acreage', value: `${fmtFixed(ds.parcel.acreage, 1)} acres` },
                { key: 'Owner', value: ds.parcel.owner },
              ];
              if (ds.parcel.assessedValue != null) {
                parcelRows.push({ key: 'Assessed Value', value: `$${formatNumber(ds.parcel.assessedValue, locale)}` });
              }
              groups.push({
                heading: 'Site Boundary & Parcel',
                rows: parcelRows,
              });
            }

            if (ds.solar || ds.wind) {
              const renewableRows: DatasheetRow[] = [];
              if (ds.solar) {
                renewableRows.push({ key: 'Solar GHI', value: `${ds.solar.ghi} kWh/m²/day` });
                renewableRows.push({ key: 'Solar DNI', value: `${ds.solar.dni} kWh/m²/day` });
              }
              if (ds.wind) {
                renewableRows.push({ key: 'Wind 100m', value: `${ds.wind.windSpeed100m} m/s` });
                renewableRows.push({
                  key: 'Wind CF (stored dataset field)',
                  value: `${fmtFixed(typeof ds.wind.capacityFactor === 'number' ? ds.wind.capacityFactor * 100 : undefined, 0)}% (unverified/unknown in stored SED payload; turbine curve, hub-height loss model, and derivation not retained)`,
                });
              }
              groups.push({ heading: 'Renewable Resource', rows: renewableRows });
            }

            if (ds.electricity) {
              groups.push({
                heading: formatElectricityPricingTitle(ds.electricity),
                rows: [
                  { key: 'Price Reference', value: formatElectricityPriceReference(ds.electricity) },
                ],
              });
            }

            if (ds.airQuality) {
              groups.push({
                heading: 'Air Quality',
                meta: `AQI ${ds.airQuality.aqi} · ${ds.airQuality.category}`,
                rows: [
                  { key: 'AQI', value: String(ds.airQuality.aqi), tone: ds.airQuality.aqi > 100 ? 'warning' : 'default' },
                  { key: 'Category', value: ds.airQuality.category },
                  { key: 'Primary Pollutant', value: ds.airQuality.primaryPollutant },
                  { key: 'PM2.5', value: ds.airQuality.pm25 != null ? `${ds.airQuality.pm25} µg/m³` : null },
                ],
              });
            }

            if (ds.connectivity) {
              const connectivity = connectivityProximityPresentation(ds.connectivity);
              groups.push({
                heading: 'Network Connectivity',
                meta: connectivity.meta,
                rows: [
                  { key: connectivity.nameLabel, value: connectivity.name },
                  { key: 'Straight-line distance', value: connectivity.distance },
                  { key: 'Recorded IXs within 50km', value: connectivity.ixInventory },
                  { key: 'DC Facilities 50km', value: String(ds.connectivity.facilityCount50km) },
                  { key: 'Coverage limitation', value: connectivity.limitation },
                ],
              });
            }

            if (groups.length === 0) return null;
            return (
              <EditorialDatasheet
                theme={theme}
                groups={groups}
                className="pb-1"
                id="extended-datasheet"
              />
            );
          })()}

          <NwiAcreageConflictPanel conflicts={ds.environmental?.wetlandSourceAcreageConflicts} issues={ds.environmental?.wetlandAcreageIssues} />

          {/* Environmental (Edit Mode) */}
          {ds.environmental && isEditing && (
            <AccordionTab title={`Environmental – ${ds.environmental.hasCriticalHabitat ? 'CRITICAL HABITAT' : typeof ds.environmental.hasCriticalHabitat !== 'boolean' ? 'Habitat Unknown' : ds.environmental.hasWetlands === undefined ? 'Wetlands Unknown' : ds.environmental.hasWetlands ? 'Wetlands Present' : 'Environmental Screening'}`} defaultOpen={true} theme={theme} editorialActive={editorialActive}>
              <div className="p-2.5 space-y-2" style={dataBoxStyle(theme, editorialActive)}>
                <div className="space-y-2">
                  <div className="space-y-1">
                    <div className="text-[9px] font-bold text-text-secondary uppercase tracking-widest">Critical Habitat</div>
                    <input className="w-full text-xs bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
                      value={typeof editedDatasets.environmental?.hasCriticalHabitat !== 'boolean' ? 'Unknown' : editedDatasets.environmental?.hasCriticalHabitat ? 'Mapped final/proposed' : 'No mapped overlap'}
                      onChange={(e) => {
                        const text = e.target.value.trim().toLowerCase();
                        const isHabitat = /^(yes|mapped final|present)/.test(text) ? true : /^(no|none|absent)/.test(text) ? false : undefined;
                        setEditedReport({ ...editedReport, datasets: { ...editedDatasets, environmental: { ...editedDatasets.environmental, hasCriticalHabitat: isHabitat, species: editedDatasets.environmental?.species || [], hasWetlands: editedDatasets.environmental?.hasWetlands, wetlandTypes: editedDatasets.environmental?.wetlandTypes || [], wetlandAcreage: editedDatasets.environmental?.wetlandAcreage } } });
                      }} />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[9px] font-bold text-text-secondary uppercase tracking-widest">Species</div>
                    <input className="w-full text-xs bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
                      value={(editedDatasets.environmental?.species || []).join(', ')}
                      onChange={(e) => {
                        const species = e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean);
                        setEditedReport({ ...editedReport, datasets: { ...editedDatasets, environmental: { ...editedDatasets.environmental, hasCriticalHabitat: editedDatasets.environmental?.hasCriticalHabitat, species, hasWetlands: editedDatasets.environmental?.hasWetlands, wetlandTypes: editedDatasets.environmental?.wetlandTypes || [], wetlandAcreage: editedDatasets.environmental?.wetlandAcreage } } });
                      }} />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[9px] font-bold text-text-secondary uppercase tracking-widest">Wetlands</div>
                    <input className="w-full text-xs bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
                      value={formatWetlandsDisplay(editedDatasets.environmental)}
                      onChange={(e) => {
                        const hasWetlands = e.target.value.toLowerCase().includes('yes');
                        const match = e.target.value.match(/[\d.]+/);
                        const acreage = match ? parseFloat(match[0]) : 0;
                        setEditedReport({ ...editedReport, datasets: { ...editedDatasets, environmental: { ...editedDatasets.environmental, hasCriticalHabitat: editedDatasets.environmental?.hasCriticalHabitat, species: editedDatasets.environmental?.species || [], hasWetlands, wetlandTypes: editedDatasets.environmental?.wetlandTypes || [], wetlandAcreage: acreage } } });
                      }} />
                  </div>
                </div>
              </div>
            </AccordionTab>
          )}

          {/* Parcel Data (Edit Mode) */}
          {ds.parcel && isEditing && (
            <AccordionTab title={`Parcel – ${ds.parcel.zoning} (${fmtFixed(ds.parcel.acreage, 0)} acres)`} defaultOpen={true} theme={theme} editorialActive={editorialActive}>
              <div className="p-2.5 space-y-2" style={dataBoxStyle(theme, editorialActive)}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2">
                  <DataCell label="Zoning" value={ds.parcel.zoning} />
                  <DataCell label="Land Use" value={ds.parcel.landUse} />
                  <DataCell label="Acreage" value={`${fmtFixed(ds.parcel.acreage, 1)} acres`} />
                  <DataCell label="Owner" value={ds.parcel.owner} />
                </div>
                {ds.parcel.assessedValue != null && (
                  <div className="pt-1" style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}` }}>
                    <DataCell label="Assessed Value" value={`$${formatNumber(ds.parcel.assessedValue, locale)}`} />
                  </div>
                )}
              </div>
            </AccordionTab>
          )}

        {/* NRC Reactor Proximity — J4: dotted-leader datasheet (meta = maturity).
             The colour-coded nearby-reactor list (power-status dot per unit)
             is preserved as a single multi-line value node on a "Nearby
             Reactors" row so the datasheet keeps the operating-status signal a
             flat ledger would otherwise drop. */}
        {ds.nrcReactors && (() => {
          const nrc = ds.nrcReactors;
          const proximity = resolveNrcProximity(nrc);
          const nuclearRows: DatasheetRow[] = [
            { key: 'Nearest Reactor', value: proximity.name ?? 'Unresolved' },
            { key: 'Straight-line distance', value: proximity.distanceKm == null ? 'Unresolved' : `${proximity.distanceKm} km` },
            { key: 'Catalog search coverage', value: proximity.summary },
            { key: 'Reactors within 200km', value: String(nrc.reactorsWithin200km) },
            {
              key: 'Infrastructure Maturity',
              value: nrc.nuclearInfraMaturity,
              tone: nrc.nuclearInfraMaturity === 'Low' ? 'negative' : 'default',
            },
          ];
          if (nrc.reactorList?.length > 0) {
            nuclearRows.push({
              key: 'Nearby Reactors',
              value: (
                <span className="flex flex-col items-end gap-1">
                  {nrc.reactorList.map((r, i) => (
                    <span key={i} className="flex items-center gap-2">
                      <span>
                        {r.name} – {r.distKm}km{r.power >= 0 ? `, ${r.power}% power` : ''}
                      </span>
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: roleColor(theme, r.power > 0 ? 'positive' : r.power === 0 ? 'negative' : 'faint') }}
                      />
                    </span>
                  ))}
                </span>
              ),
            });
          }
          return (
            <EditorialDatasheet
              theme={theme}
              groups={[{
                heading: 'Nuclear Infrastructure',
                meta: `${nrc.nuclearInfraMaturity} maturity`,
                rows: nuclearRows,
              }]}
              className="pb-1"
              id="nuclear-infrastructure-datasheet"
            />
          );
        })()}

        {/* Site-Specific Research — J4: Water Rights / Tribal / Local
             Infrastructure / State Permitting now render as dotted-leader
             EditorialDatasheet groups (key · leader · right-aligned value,
             optional right-aligned meta tag), matching the Seismic /
             Environmental / Renewable treatment. Null/absent values drop the
             row (never fabricated). Nearby Hazards (multi-list) + Siting
             Precedents (narrative) KEEP their card layouts where a flat
             ledger doesn't fit. The "AI-Sourced — Verify" labelling is
             preserved on the AccordionTab title. */}
        {ds.siteResearch && (
          <AccordionTab
            title="Site-Specific Research (AI-Sourced – Verify)"
            defaultOpen={true}
            theme={theme}
            editorialActive={editorialActive}
            collapsible={false}
            pagedStaticGroupId="site-specific-research"
          >
            <div className="space-y-5">

              {/* Water Rights · Tribal Consultation · Local Infrastructure ·
                   State Permitting — composed as one datasheet so the groups
                   share the dotted-leader rhythm and thin groups render
                   cleanly. */}
              {(() => {
                const sr = ds.siteResearch!;
                const groups: DatasheetGroup[] = [];

                // Water Rights
                groups.push({
                  heading: 'Water Rights',
                  rows: [
                    { key: 'Doctrine', value: formatDoctrineDisplay(sr.waterRights.doctrine) },
                    { key: 'Nearest Water', value: sr.waterRights.nearestWaterSource },
                    { key: 'Authority', value: sr.waterRights.authority },
                    {
                      key: 'Constraints',
                      value: neutralizeUnverifiedWaterRegulatoryThresholdText(
                        sr.waterRights.constraints,
                      ),
                    },
                  ],
                });

                // Tribal Consultation — meta tag = required / not-required
                if (sr.tribalLands?.consultationRequired || sr.tribalLands?.nearbyTribes?.length > 0) {
                  groups.push({
                    heading: 'Tribal Consultation',
                    meta: researchConsultationLabel(sr.tribalLands?.consultationRequired).toUpperCase(),
                    rows: [
                      { key: 'Nearby Tribes', value: sr.tribalLands?.nearbyTribes?.length ? sr.tribalLands.nearbyTribes.join(', ') : null },
                      { key: 'Details', value: sr.tribalLands.details },
                    ],
                  });
                }

                // Local Infrastructure
                groups.push({
                  heading: 'Local Infrastructure',
                  rows: [
                    { key: 'Electric Utility', value: sr.localInfrastructure.utilityProvider },
                    { key: 'Nearest Interstate', value: sr.localInfrastructure.nearestInterstate },
                    { key: 'Rail Access', value: sr.localInfrastructure.railAccess },
                    { key: 'Port Access', value: sr.localInfrastructure.portAccess || 'None' },
                  ],
                });

                // State Permitting — meta tag = moratorium status
                groups.push({
                  heading: 'State Permitting',
                  meta: sr.statePermitting.nuclearMoratorium === true ? 'MORATORIUM' : sr.statePermitting.nuclearMoratorium === false ? 'NO MORATORIUM' : 'MORATORIUM STATUS UNVERIFIED',
                  rows: [
                    { key: 'Estimated Timeline', value: sr.statePermitting.estimatedTimeline },
                    { key: 'Key Permits Required', value: sr.statePermitting.keyPermits?.length ? sr.statePermitting.keyPermits.join(', ') : null },
                  ],
                });

                return (
                  <EditorialDatasheet
                    theme={theme}
                    groups={groups}
                    // Site-research values are paragraph-scale. Pairing two
                    // long values creates one oversized, unfragmentable flex
                    // row that Paged.js clips at the footer. Full-width row
                    // atoms remain short enough to move across page seams.
                    rowLayout="single"
                    id="site-research-datasheet"
                  />
                );
              })()}

              {/* Nearby Hazards */}
              {(ds.siteResearch?.nearbyHazards?.airports?.length > 0 || ds.siteResearch?.nearbyHazards?.militaryBases?.length > 0 || ds.siteResearch?.nearbyHazards?.dams?.length > 0) && (
                <div className="space-y-2 p-2.5" style={dataBoxStyle(theme, editorialActive)}>
                  <div className="text-[10px] font-bold uppercase tracking-widest pb-1" style={{ ...microLabelStyle(theme), borderBottom: `1px solid ${roleColor(theme, 'hair')}` }}>Nearby Hazards (NRC External Hazard Assessment)</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {ds.siteResearch?.nearbyHazards?.airports?.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-[9px] font-bold text-text-secondary uppercase tracking-widest">Airports</div>
                        {ds.siteResearch.nearbyHazards.airports.map((a, i) => <div key={i} className="text-xs text-text-secondary leading-relaxed">{a}</div>)}
                      </div>
                    )}
                    {ds.siteResearch?.nearbyHazards?.militaryBases?.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-[9px] font-bold text-text-secondary uppercase tracking-widest">Military</div>
                        {ds.siteResearch.nearbyHazards.militaryBases.map((m, i) => <div key={i} className="text-xs text-text-secondary leading-relaxed">{m}</div>)}
                      </div>
                    )}
                    {ds.siteResearch?.nearbyHazards?.dams?.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-[9px] font-bold text-text-secondary uppercase tracking-widest">Dams</div>
                        {ds.siteResearch.nearbyHazards.dams.map((d, i) => <div key={i} className="text-xs text-text-secondary leading-relaxed">{d}</div>)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Precedents */}
              {ds.siteResearch?.precedents?.length > 0 && (
                <div className="space-y-2 p-2.5" style={dataBoxStyle(theme, editorialActive)}>
                  <div className="text-[10px] font-bold uppercase tracking-widest pb-1" style={{ ...microLabelStyle(theme), borderBottom: `1px solid ${roleColor(theme, 'hair')}` }}>Siting Precedents</div>
                  <div className="space-y-3">
                    {ds.siteResearch.precedents.map((p, i) => (
                      <div key={i} className="space-y-0.5">
                        {/* William QA 2026-06-28 — left accent rule removed report-wide */}
                        <div className="text-xs font-medium text-text-secondary leading-relaxed">{p.description}</div>
                        <div className="text-[10px] text-text-muted leading-relaxed italic">{p.relevance}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </AccordionTab>
        )}

        {/* Site boundary metrics — ELV-1: render the accordion when the
             report carries ANY of: a persisted siteElevationRange (real
             or legacy-zero), a SED siteProfile that resolves to an EPQS
             fallback, or a snapshot boundary (so "Refresh elevation"
             can re-fetch via the polygon path). Pre-ELV this gated on
             `report.siteElevationRange` only — pre-EL2 legacy sites had
             `{0,0,0}` stamped on them, hit the inner "Data unavailable"
             branch, and had no escape hatch. The composite gate keeps
             the accordion visible for the Refresh CTA path even on
             sites whose triple was never written. */}
        {(report.siteElevationRange || resolveSiteElevation(report, sed) || (report.siteBoundary?.length ?? 0) >= 3) && (
          // FLATTEN-TO-CREAM (William QA): in READ/export mode the Site
          // Boundary renders BARE (the EditorialDatasheet below, matching the
          // Electricity-Pricing / Renewable sibling sections — no boxed
          // container / header bar / chevron). The AccordionTab chrome is kept
          // ONLY for edit mode, where the collapsible edit grid needs a shell.
          isEditing ? (
            <AccordionTab
              title={`Site Boundary – ${report.siteArea?.toFixed(0) ?? '?'} acres`}
              defaultOpen={true}
              theme={theme}
              editorialActive={editorialActive}
            >
              <div className="p-3 grid grid-cols-2 sm:grid-cols-4 gap-3" style={dataBoxStyle(theme, editorialActive)}>
                <div className="space-y-0.5">
                  <div className="text-[9px] font-bold text-text-secondary uppercase tracking-widest">Area</div>
                  <input className="w-full text-xs font-mono bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
                    value={editedReport.siteArea?.toFixed(1) ?? ''} onChange={(e) => setEditedReport({ ...editedReport, siteArea: parseFloat(e.target.value) || 0 })} />
                </div>
                <div className="space-y-0.5">
                  <div className="text-[9px] font-bold text-text-secondary uppercase tracking-widest">Perimeter (km)</div>
                  <input className="w-full text-xs font-mono bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
                    value={editedReport.sitePerimeter?.toFixed(2) ?? ''} onChange={(e) => setEditedReport({ ...editedReport, sitePerimeter: parseFloat(e.target.value) || 0 })} />
                </div>
                <div className="space-y-0.5">
                  <div className="text-[9px] font-bold text-text-secondary uppercase tracking-widest">Elevation Range</div>
                  <input className="w-full text-xs font-mono bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
                    value={`${editedReport.siteElevationRange?.min ?? ''}–${editedReport.siteElevationRange?.max ?? ''}`}
                    onChange={(e) => {
                      const parts = e.target.value.split('–').map((s: string) => parseFloat(s.trim()));
                      setEditedReport({ ...editedReport, siteElevationRange: { ...editedReport.siteElevationRange!, min: parts[0] || 0, max: parts[1] || 0 } });
                    }} />
                </div>
                <div className="space-y-0.5">
                  <div className="text-[9px] font-bold text-text-secondary uppercase tracking-widest">Avg Elevation (m ASL)</div>
                  <input className="w-full text-xs font-mono bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
                    value={editedReport.siteElevationRange?.avg ?? ''} onChange={(e) => setEditedReport({ ...editedReport, siteElevationRange: { ...editedReport.siteElevationRange!, avg: parseFloat(e.target.value) || 0 } })} />
                </div>
              </div>
            </AccordionTab>
          ) : (() => {
              // ELV-1 — resolve elevation through the shared helper.
              // Priority: report.siteElevationRange (real triple) →
              // sed.siteProfile.data.elevationAvg (USGS EPQS centroid) →
              // null (legacy `{0,0,0}` with no SED fallback).
              //
              // The pre-ELV branch surfaced "Data unavailable" for
              // every legacy zero-triple site — this resolver path
              // recovers the SED reading without re-fetching, and
              // the Refresh button (below the cells) lets the user
              // upgrade to a polygon sample on demand.
              //
              // ELV-4 lifts `resolved` out of the inner IIFE so the
              // diagnostic chip below can render only when the elevation
              // path collapsed to "Data unavailable".
              const resolved = resolveSiteElevation(report, sed);
              const isEmpty = resolved === null;
              // ELV-4 — only stamp the chip when (a) the elevation cells
              // ARE rendering the empty path, (b) the caller passed a
              // diagnosis (legacy viewers without an attempt log
              // suppress), and (c) we're not actively refreshing.
              const showDiagnosticChip =
                isEmpty && elevationEmptyDiagnosis != null && !isRefreshingElevation;
              const ago = showDiagnosticChip ? formatAgo(elevationLastAttemptedAt) : null;
              // J4 — Site Boundary as a dotted-leader datasheet group (area,
              // elevation range, perimeter, avg elevation, source) matching
              // the Seismic / Renewable / Environmental treatment. Elevation
              // values keep their `data-testid` markers (wrapped on the value
              // node) so the ELV-1/ELV-4 regression assertions still bind; an
              // absent elevation drops to the "Data unavailable" testid'd
              // value (never fabricated). The ElevationDiagnostic source row +
              // empty-reason chip + Refresh CTA render below the datasheet.
              // William QA 2026-06-28 — fall back through every area source so the
              // Site Boundary section shows the known acreage even when no polygon
              // boundary was drawn (siteArea / siteBoundaryAggregate are polygon-
              // derived and empty for a point-only site; the parcel dataset carries
              // the surveyed acreage). Perimeter needs a polygon, so it stays "—"
              // when none exists rather than fabricating a number.
              const finiteNum = (n: unknown): number | null =>
                typeof n === 'number' && Number.isFinite(n) ? n : null;
              const areaAcres =
                finiteNum(report.siteArea)
                ?? finiteNum(report.siteBoundaryAggregate?.totalArea)
                ?? finiteNum(report.datasets?.parcel?.acreage);
              const perimeterKm =
                finiteNum(report.sitePerimeter)
                ?? finiteNum(report.siteBoundaryAggregate?.totalPerimeter);
              const boundaryRows: DatasheetRow[] = [
                { key: 'Area', value: `${areaAcres != null ? areaAcres.toFixed(1) : '–'} acres` },
                { key: 'Perimeter', value: `${perimeterKm != null ? perimeterKm.toFixed(2) : '–'} km` },
              ];
              if (!resolved) {
                boundaryRows.push({
                  key: 'Elevation Range',
                  value: <span data-testid="elevation-empty-range">Data unavailable</span>,
                });
                boundaryRows.push({
                  key: 'Avg Elevation',
                  value: <span data-testid="elevation-empty-avg">Data unavailable</span>,
                });
              } else {
                const { value, source } = resolved;
                const isCentroidPoint =
                  source === 'usgs-epqs-centroid' &&
                  value.min === value.max &&
                  value.max === value.avg;
                // Honest source label: centroid-only samples collapse to a
                // single number with a "(centroid, USGS)" suffix; polygon-
                // sample renders the full range as before, optionally with
                // the sample count when EL3 stats are present.
                const rangeLabel = isCentroidPoint
                  ? `${Math.round(value.avg)}m (centroid, USGS)`
                  : `${Math.round(value.min)}–${Math.round(value.max)}m`;
                const n = value.pointsSampled;
                const avgLabel = isCentroidPoint
                  ? `${Math.round(value.avg)}m ASL (USGS)`
                  : typeof n === 'number' && n > 0
                    ? `${Math.round(value.avg)}m ASL (n=${n})`
                    : `${Math.round(value.avg)}m ASL`;
                boundaryRows.push({
                  key: 'Elevation Range',
                  value: <span data-testid={`elevation-range-${source}`}>{rangeLabel}</span>,
                });
                boundaryRows.push({
                  key: 'Avg Elevation',
                  value: <span data-testid={`elevation-avg-${source}`}>{avgLabel}</span>,
                });
              }
              return (
                // FLATTEN-TO-CREAM (William QA) — render the Site Boundary
                // datasheet BARE on the cream page (no boxed fill / hairline
                // frame) so it matches the clean Electricity-Pricing / Renewable
                // datasheet rows above, which compose into the unwrapped
                // EditorialDatasheet. The elevation diagnostic + Refresh CTA
                // below stay grouped by spacing, not a grey container.
                <div>
                  <EditorialDatasheet
                    theme={theme}
                    groups={[{ heading: 'Site Boundary', rows: boundaryRows }]}
                    id="site-boundary-datasheet"
                  />
                  {/* ELV-4 — diagnostic surface. Mirrors `siteElevationSource`
                       (gen-time stamp) for the source attribution; pulls
                       `siteElevationRefreshedAt` (live-site stamp) for the
                       "last refreshed N ago" affordance. When the report's
                       own resolver returns null (legacy {0,0,0} + no SED
                       fallback), the empty-state hint renders with the
                       Refresh CTA below. */}
                  {(() => {
                    const sourceTag = resolved?.source
                      ?? (report.siteElevationSource ?? null);
                    return (
                      <ElevationDiagnostic
                        source={sourceTag}
                        cascadeSource={(report.siteElevationRange as { cascadeSource?: string } | undefined)?.cascadeSource}
                        pointsSampled={report.siteElevationRange?.pointsSampled}
                        refreshedAt={siteElevationRefreshedAt}
                      />
                    );
                  })()}
                  {/* ELV-4 — empty-reason classification chip. Surfaces ONLY when
                       the resolver returned null AND the caller supplied a
                       diagnosis (e.g. captured during the most recent
                       refreshSiteElevation roundtrip). Uses an unobtrusive
                       amber-tinted text row so it doesn't compete with the
                       Refresh CTA. Distinct from `ElevationDiagnostic` above,
                       which surfaces source attribution when elevation HAS a
                       value; this chip surfaces root-cause when it DOESN'T. */}
                  {showDiagnosticChip && elevationEmptyDiagnosis && (
                    <div
                      className="mt-3 text-[10px] uppercase tracking-widest px-2 py-1.5 flex items-start gap-1.5"
                      // N1.5C-flat (EDITORIAL-MODE ONLY) — the elevation diagnostic
                      // flattens to an open warning note: drop the `warningSoft`
                      // fill + the all-sides 1px border for a transparent bg + a
                      // 3px WARNING left-rule (keeps the warning semantic; matches
                      // the editorialFlat left-rule treatment). Flag OFF keeps the
                      // filled+bordered amber chip byte-identical.
                      style={
                        editorialActive
                          ? {
                              // William QA 2026-06-28 — left accent rule removed report-wide
                              color: roleColor(theme, 'warning'),
                              background: 'transparent',
                            }
                          : {
                              color: roleColor(theme, 'warning'),
                              background: roleColor(theme, 'warningSoft'),
                              border: `1px solid ${roleBorder(theme, 'warning')}`,
                            }
                      }
                      data-testid="elevation-empty-reason"
                      data-classification={elevationEmptyDiagnosis.classification}
                      role="status"
                    >
                      <AlertTriangle
                        aria-hidden="true"
                        data-pdf-elevation-warning-mark="true"
                        className="mt-[0.1em] h-[1em] w-[1em] shrink-0"
                        strokeWidth={1.75}
                      />
                      <span className="flex-1 normal-case tracking-normal">
                        <span className="font-semibold">
                          Elevation – {describeElevationEmpty(elevationEmptyDiagnosis.classification)}.
                        </span>
                        {ago && (
                          <span className="ml-1" style={{ color: roleColorAlpha(theme, 'warning', 0.85) }}>
                            Last attempted {ago}.
                          </span>
                        )}
                        {elevationEmptyDiagnosis.hint && (
                          <span className="block mt-0.5 text-[10px]" style={{ color: roleColorAlpha(theme, 'warning', 0.85) }}>
                            {elevationEmptyDiagnosis.hint}
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  {/* BUG-10 — the "Refresh elevation" CTA was REMOVED from the
                       report surface. This Site Boundary block renders ONLY in
                       the read-only (`!isEditing`) branch, which IS the rendered
                       report / PDF view, so the interactive control leaked into
                       the static report whenever a caller passed a handler. The
                       editor branch (`isEditing`, above) never carried the CTA,
                       so it has no in-editor home; an interactive re-fetch
                       button does not belong on a read-only report surface.
                       `onRefreshElevation` / `isRefreshingElevation` remain on
                       the prop contract (still consumed by the diagnostic-chip
                       suppression logic above) but no longer render a button. */}
                </div>
              );
            })()
        )}
        </div>
  );

  // COMBINES (fold #5) — appendix-subsection mode. No EditorialSectionShell,
  // no section number / hero numeral, no studioStyle (visibility + order are
  // parent-owned by the appendix shell). The wrapper KEEPS
  // `data-section="extendedData"` — a NESTED data-section inside the appendix
  // is the established reconcile-safe shape (same as `dataSources` and the
  // gridStudy ELCC fold): the export DOM reorder + pagination snapshot walk
  // TOP-LEVEL section nodes only, so this box travels with the appendix. The
  // sub-head matches ReferencesSection's appendix siblings (h3, small-caps,
  // `appendixHeadStyle`) and stays heading-override aware via the same
  // EditableHeading key (`inline` — the wrapper owns the `#extendedData`
  // anchor, so the heading span must not duplicate the id).
  if (appendixFold) {
    return (
      <div
        id="extendedData"
        data-section="extendedData"
        data-section-id="extendedData"
        data-subsection="extendedData"
        className="space-y-3"
        {...layoutGroupProps('row-group')}
        style={{ scrollMarginTop: '80px' }}
        data-testid="extended-data-appendix-subsection"
      >
        <h3
          className="text-sm font-bold uppercase tracking-widest"
          data-heading="true"
          style={appendixHeadStyle(theme)}
        >
          <EditableHeading
            headingKey="extendedData"
            defaultText="Extended Site Data"
            isEditing={isEditing}
            overrides={headingOverrides}
            onChange={setHeading}
            inline
          />
        </h3>
        {extendedBody}
      </div>
    );
  }

  return (
    <EditorialSectionShell
      id={sectionId}
      data-page="extended"
      data-section={sectionId}
      data-section-columns={sectionPageColumnCount ? String(sectionPageColumnCount(sectionId)) : undefined}
      extraClassName={reportSectionGapClass(theme.density)}
      style={{ ...studioStyle, fontFamily: theme.bodyFont, color: roleColor(theme, 'body'), scrollMarginTop: '80px', borderBottom: `1px solid ${roleColor(theme, 'hair')}` }}
      omitSectionSidebar={omitSectionSidebar}
      shellPadding={shellPadding}
      theme={theme}
      {...sectionNumberProps}
      {...shellModeProps}
    >
      <div className="space-y-3">
        {classicHead}

        {extendedBody}
      </div>
    </EditorialSectionShell>
  );
});

ExtendedDataSection.displayName = 'ExtendedDataSection';

export { ExtendedDataSection };
